import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowLeft, Ruler, User, CheckCircle2, Clock, ChevronLeft, ChevronRight, X, Tag } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = process.env.REACT_APP_BACKEND_URL;
const categoryLabels = { konut: "Konut", ticari: "Ticari", endustriyel: "Endüstriyel", altyapi: "Alt Yapı" };

export default function ProjeDetayPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/projects/${id}`).then((r) => {
      setProject(r.data);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white" data-testid="project-detail-loading">
        <Navbar />
        <div className="pt-32 flex justify-center">
          <div className="w-8 h-8 border-3 border-[#FF5A00]/30 border-t-[#FF5A00] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white" data-testid="project-detail-notfound">
        <Navbar />
        <div className="pt-32 text-center px-5">
          <h1 className="text-2xl font-bold text-gray-800 mb-4" style={{ fontFamily: "Outfit" }}>Proje Bulunamadı</h1>
          <button onClick={() => navigate("/projeler")} className="btn-modern btn-filled text-sm"><ArrowLeft size={16} /> Projelere Dön</button>
        </div>
        <Footer />
      </div>
    );
  }

  const allImages = [project.image, ...(project.images || [])].filter(Boolean);
  const isOngoing = project.status === "ongoing";
  const statusLabel = isOngoing ? "Devam Ediyor" : "Tamamlandı";
  const statusColor = isOngoing ? "bg-blue-600 text-white" : "bg-emerald-600 text-white";

  const prevImg = () => setActiveImg((p) => (p - 1 + allImages.length) % allImages.length);
  const nextImg = () => setActiveImg((p) => (p + 1) % allImages.length);

  return (
    <div className="min-h-screen bg-white" data-testid="project-detail-page">
      <Navbar />

      {/* Hero */}
      <div className="pt-20 bg-gradient-to-b from-[#0C0C0C] to-[#1a1a2e]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8 sm:py-12 lg:py-16">
          <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} onClick={() => navigate("/projeler")} className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-4 sm:mb-6 transition-colors" data-testid="back-to-projects">
            <ArrowLeft size={16} /> Projelere Dön
          </motion.button>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white tracking-tighter" style={{ fontFamily: "Outfit" }} data-testid="project-detail-title">
            {project.title}
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 sm:mt-4">
            <span className="flex items-center gap-1.5 text-xs sm:text-sm text-white/50"><MapPin size={14} /> {project.location}</span>
            <span className="flex items-center gap-1.5 text-xs sm:text-sm text-white/50"><Calendar size={14} /> {project.date}</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${statusColor}`}>
              {isOngoing ? <Clock size={12} /> : <CheckCircle2 size={12} />} {statusLabel}
            </span>
            {project.category && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/70">
                <Tag size={12} /> {categoryLabels[project.category] || project.category}
              </span>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8 sm:py-12 lg:py-16">
        <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-10">
          {/* Left: Images */}
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-xl sm:rounded-2xl overflow-hidden bg-gray-100 cursor-pointer group" onClick={() => setLightbox(true)} data-testid="project-main-image">
              <img src={allImages[activeImg]} alt={project.title} className="w-full aspect-[16/10] object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
              {allImages.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="img-prev">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="img-next">
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
              <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full">
                {activeImg + 1} / {allImages.length}
              </div>
            </motion.div>

            {allImages.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2" data-testid="project-thumbnails">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? "border-[#FF5A00] shadow-md" : "border-transparent opacity-60 hover:opacity-100"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <div className="mt-6 sm:mt-8">
              <h2 className="text-lg sm:text-xl font-bold text-[#0C0C0C] mb-3 sm:mb-4" style={{ fontFamily: "Outfit" }}>Proje Hakkında</h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{project.long_description || project.description}</p>
            </div>

            {project.features && project.features.length > 0 && (
              <div className="mt-6 sm:mt-8">
                <h2 className="text-lg sm:text-xl font-bold text-[#0C0C0C] mb-3 sm:mb-4" style={{ fontFamily: "Outfit" }}>Proje Özellikleri</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {project.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`feature-${i}`}>
                      <CheckCircle2 size={16} className="text-[#FF5A00] flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 font-medium">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Details sidebar */}
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:sticky lg:top-28" data-testid="project-sidebar">
              <h3 className="text-base sm:text-lg font-bold text-[#0C0C0C] mb-5 sm:mb-6" style={{ fontFamily: "Outfit" }}>Proje Detayları</h3>

              <div className="space-y-4 sm:space-y-5">
                {/* Status */}
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isOngoing ? "bg-blue-100" : "bg-emerald-100"}`}>
                    {isOngoing ? <Clock size={16} className="text-blue-600" /> : <CheckCircle2 size={16} className="text-emerald-600" />}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Durum</p>
                    <p className={`text-sm font-bold mt-0.5 ${isOngoing ? "text-blue-600" : "text-emerald-600"}`}>{statusLabel}</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-[#FF5A00]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Konum</p>
                    <p className="text-sm text-gray-800 font-medium mt-0.5">{project.location}</p>
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center flex-shrink-0">
                    <Calendar size={16} className="text-[#FF5A00]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Proje Tarihi</p>
                    <p className="text-sm text-gray-800 font-medium mt-0.5">{project.date}</p>
                    {project.start_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Başlangıç: {project.start_date}
                        {project.end_date ? ` — Bitiş: ${project.end_date}` : " — Devam ediyor"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Category */}
                {project.category && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center flex-shrink-0">
                      <Tag size={16} className="text-[#FF5A00]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Kategori</p>
                      <p className="text-sm text-gray-800 font-medium mt-0.5">{categoryLabels[project.category] || project.category}</p>
                    </div>
                  </div>
                )}

                {/* Area */}
                {project.area && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center flex-shrink-0">
                      <Ruler size={16} className="text-[#FF5A00]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Proje Alanı</p>
                      <p className="text-sm text-gray-800 font-medium mt-0.5">{project.area}</p>
                    </div>
                  </div>
                )}

                {/* Client */}
                {project.client_name && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-[#FF5A00]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Müşteri</p>
                      <p className="text-sm text-gray-800 font-medium mt-0.5">{project.client_name}</p>
                    </div>
                  </div>
                )}

                {/* Progress */}
                {isOngoing && project.progress > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">İlerleme</p>
                      <span className="text-sm font-bold text-[#FF5A00]">%{project.progress}</span>
                    </div>
                    <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${project.progress}%` }} transition={{ duration: 1.2, delay: 0.3 }} className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FF8A3D] rounded-full" />
                    </div>
                  </div>
                )}

                {!isOngoing && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl p-3">
                      <CheckCircle2 size={18} />
                      <span className="text-sm font-semibold">Proje Başarıyla Tamamlandı</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 sm:mt-8">
                <a href="/iletisim" className="btn-modern btn-filled w-full justify-center text-sm py-3">
                  Bilgi Alın
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" data-testid="image-lightbox" onClick={() => setLightbox(false)}>
          <button className="absolute top-4 right-4 text-white/60 hover:text-white z-10" onClick={() => setLightbox(false)} data-testid="lightbox-close">
            <X size={28} />
          </button>
          {allImages.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center z-10 transition-colors">
                <ChevronLeft size={22} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center z-10 transition-colors">
                <ChevronRight size={22} />
              </button>
            </>
          )}
          <img src={allImages[activeImg]} alt={project.title} className="max-w-[95vw] sm:max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {activeImg + 1} / {allImages.length}
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
